<template>
  <div class="pk">
    <div class="pk-head">
      <div class="pk-head__left">
        <el-input
          v-model="keyword"
          size="small"
          clearable
          prefix-icon="el-icon-search"
          :placeholder="$t('subAccount.picker.searchMenu')"
        />
      </div>
      <div class="pk-head__right">
        <span class="pk-head__count">{{ $t('subAccount.picker.selectedCount', { n: value.length }) }}</span>
        <span
          class="pk-head__clear"
          :class="{ 'is-disabled': !value.length }"
          @click="clearAll"
        >{{ $t('subAccount.picker.clearAll') }}</span>
      </div>
    </div>

    <div class="pk-body">
      <div class="pk-col">
        <div v-if="selectableKeys.length" class="pk-row">
          <el-checkbox
            :value="allChecked"
            :indeterminate="allIndeterminate"
            @change="toggleAll"
          >{{ $t('subAccount.picker.selectAll') }}</el-checkbox>
        </div>

        <template v-for="group in filteredTree">
          <!-- 一级菜单本身就是权限位（「数据」）：单独占一行、和其他一级同级，
               不缩进也不会有半选态（PRD 4.1.2 第 4.b 条） -->
          <div v-if="group.leaf" :key="group.key" class="pk-row">
            <el-checkbox
              :value="isChecked(group.key)"
              @change="toggle(group.key)"
            >{{ group.label }}</el-checkbox>
          </div>

          <div v-else :key="group.key">
            <div class="pk-row">
              <el-checkbox
                :value="groupChecked(group)"
                :indeterminate="groupIndeterminate(group)"
                @change="toggleGroup(group)"
              >{{ group.label }}</el-checkbox>
            </div>

            <div v-for="child in group.children" :key="child.key" class="pk-row pk-row--child">
              <el-checkbox
                :value="isChecked(child.key)"
                @change="toggle(child.key)"
              >{{ child.label }}</el-checkbox>
            </div>
          </div>
        </template>

        <p v-if="!filteredTree.length" class="pk-empty">{{ $t('subAccount.picker.noMatch') }}</p>
      </div>

      <div class="pk-col pk-col--selected">
        <div v-for="item in selectedList" :key="item.key" class="pk-row">
          <span class="pk-row__label">
            <span v-if="item.group" class="pk-row__group">{{ item.group }}</span>
            <span v-if="item.group" class="pk-row__sep">/</span>
            {{ item.label }}
          </span>
          <i class="el-icon-close pk-row__remove" @click="toggle(item.key)"></i>
        </div>
        <p v-if="!selectedList.length" class="pk-empty">{{ $t('subAccount.picker.noMenuSelected') }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import perm from '@/utils/permission'

export default {
  name: 'MenuPermissionPicker',
  props: {
    // 选中的 menu_key 数组，直接就是提交给后端的 menu_keys
    value: {
      type: Array,
      default: () => []
    },
    // 可授权菜单树（[{ menu_key, name, children }]），由登录下发的菜单树滤出来
    tree: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      keyword: ''
    }
  },
  computed: {
    // 后端节点先摊成 { key, label, children } —— 模板里就不用到处 menu_key/name 了。
    // label 优先用前端的双语文案，认不出的 key 才退回后端的 name（见 permission.js）
    groups() {
      return this.tree.map(top => {
        const children = top.children || []
        return {
          key: perm.menuKey(top),
          label: perm.menuLabel(top),
          leaf: !children.length,
          children: children.map(c => ({ key: perm.menuKey(c), label: perm.menuLabel(c) }))
        }
      })
    },
    // 搜索命中一级菜单名时带出它的全部二级（PRD 4.1.2 第 4.c 条）
    filteredTree() {
      const kw = this.keyword.trim().toLowerCase()
      if (!kw) return this.groups
      const hit = []
      this.groups.forEach(group => {
        if (group.label.toLowerCase().indexOf(kw) !== -1) {
          hit.push(group)
          return
        }
        if (group.leaf) return
        const children = group.children.filter(c => c.label.toLowerCase().indexOf(kw) !== -1)
        if (children.length) hit.push(Object.assign({}, group, { children: children }))
      })
      return hit
    },
    // 「全选」只作用于当前搜索结果
    selectableKeys() {
      const out = []
      this.filteredTree.forEach(group => {
        if (group.leaf) out.push(group.key)
        else group.children.forEach(c => out.push(c.key))
      })
      return out
    },
    checkedSelectable() {
      return this.selectableKeys.filter(k => this.isChecked(k)).length
    },
    allChecked() {
      return this.selectableKeys.length > 0 && this.checkedSelectable === this.selectableKeys.length
    },
    allIndeterminate() {
      return this.checkedSelectable > 0 && !this.allChecked
    },
    // 右栏按菜单树顺序排，勾选顺序不影响展示，避免同一份权限在两次打开时排得不一样
    selectedList() {
      const out = []
      this.groups.forEach(group => {
        if (group.leaf) {
          if (this.isChecked(group.key)) out.push({ key: group.key, group: '', label: group.label })
          return
        }
        group.children.forEach(c => {
          if (this.isChecked(c.key)) out.push({ key: c.key, group: group.label, label: c.label })
        })
      })
      return out
    }
  },
  methods: {
    isChecked(key) {
      return this.value.indexOf(key) !== -1
    },
    emit(keys) {
      this.$emit('input', keys)
      this.$emit('change', keys)
    },
    toggle(key) {
      const keys = this.value.slice()
      const i = keys.indexOf(key)
      if (i === -1) keys.push(key)
      else keys.splice(i, 1)
      this.emit(keys)
    },
    groupChecked(group) {
      return group.children.length > 0 && group.children.every(c => this.isChecked(c.key))
    },
    groupIndeterminate(group) {
      const checked = group.children.filter(c => this.isChecked(c.key)).length
      return checked > 0 && checked < group.children.length
    },
    // 点一级菜单是整组全选 / 全不选
    toggleGroup(group) {
      this.applyKeys(group.children.map(c => c.key), !this.groupChecked(group))
    },
    toggleAll() {
      this.applyKeys(this.selectableKeys, !this.allChecked)
    },
    applyKeys(keys, on) {
      if (!keys.length) return
      const next = this.value.slice()
      keys.forEach(k => {
        const i = next.indexOf(k)
        if (on && i === -1) next.push(k)
        if (!on && i !== -1) next.splice(i, 1)
      })
      this.emit(next)
    },
    clearAll() {
      if (!this.value.length) return
      this.emit([])
    }
  }
}
</script>

<style lang="scss" scoped>
@import './picker.scss';
</style>
